import { Browser } from "@capacitor/browser";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import {
  LoaderFunction,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { ReferralBanner } from "@/atoms/banner/ReferralBanner";
import {
  ANIMATION_DURATION,
  ANIMATION_EASE,
} from "@/constants/animations.constants";
import { GiftCardAcknowledgeModal } from "@/modules/giftCards";
import { ReferralRewardsAcknowledgeRewardsModal } from "@/modules/referralRewards";
import { ReferralRewardsCard } from "@/modules/referralRewards/ReferralRewardsCard/ReferralRewardsCard";
import { RewardProgressV2 } from "@/modules/rewards/RewardProgressV2/RewardProgressV2";
import { LocalStorageService } from "@/modules/storage/StorageService/StorageService";
import TopNavV3 from "@/molecules/navbars/TopNavV3";
import { get, post } from "api";
import type { Models } from "api/types";
import { RewardStatus } from "api/types";
import { AffirmBanner } from "atoms/affirm/affirmBanner/AffirmBanner";
import { Banner } from "atoms/banner/Banner";
import Quiz from "atoms/icon/icons/ic_quiz_ol.svg";
import Discover from "atoms/icon/icons/offers_nav.svg";
import Star from "atoms/icon/icons/star.svg";
import { AnimatedBackground } from "components/AnimatedBackground";
import useCheckin from "hooks/useCheckin";
import useDunningMembership from "hooks/useDunningMemberships";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import AdonisCTAVariation1 from "modules/adonis/AdonisCTAVariation1/AdonisCTAVariation1";
import AdonisDynamicCTABanner from "modules/adonis/AdonisDynamicCTABanner/AdonisDynamicCTABanner";
import { AdvisorCTA } from "modules/adonis/AdvisorCTA/AdvisorCTA";
import { AdvisorMissingOutCarousel } from "modules/adonis/AdvisorMissingOut/AdvisorMissingOutCarousel";
import { AdvisorRecommandationCarousel } from "modules/adonis/AdvisorRecommandationCarousel/AdvisorRecommandationCarousel";
import { transformRecommendationsToSlides } from "modules/adonis/AdvisorRecommandationCarousel/transformRecommendations";
import { useAdonisDynamicCTABanner } from "modules/adonis/useAdonis/useAdonisDynamicCTABanner";
import { Analytics, AnalyticsEvents } from "modules/analytics";
import { PersonalisationIntroContainer_PATH } from "modules/personalisation/containers/PersonalisationIntroContainer";
import { RetailProductsCTA } from "modules/retailProducts";
import { useUserPreferences } from "modules/user/useUser/useUser";
import { AffirmModal } from "molecules/affirm-modal/AffirmModal";
import { BannersContainer } from "molecules/containers/banners-container/BannersContainer";
import { RewardProgress } from "molecules/containers/reward-progress/RewardProgress";
import TopNav from "molecules/navbars/TopNav";
import CampaignCard from "organisms/campaign-card/CampaignCard";
import Carousel from "organisms/carousel-with-breadcrumb/Carousel";
import MembershipPromo from "organisms/memberships-promo/MembershipsPromo";
import ProductCarousel from "organisms/product-carousel/ProductCarousel";
import { ReferAFriend } from "organisms/refer-a-friend/ReferAFriend";
import BannerCTA from "organisms/shop/BannerCTA";
import PromotionHero from "organisms/shop/site-wide-promotion-hero/PromotionHero";
import ShowSiteWidePromotion from "organisms/site-wide-promotion-modal/SiteWidePromotionModal";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import {
  checkIfListingsHaveConcerns,
  getBookAppointmentAction,
} from "services/HelperService";
import { getListingsWithDiscountInformation } from "services/ListingHelperService";
import Services from "services/Services";
import { formatAddressNoCountry } from "toolbox/Address";
import { notFalsy } from "toolbox/Guards";
import { formatPhoneNumber } from "toolbox/PhoneNumber";
import { waitObj } from "toolbox/Promise";
import { getDeviceInfo } from "utils/device.helpers";

const load = async () => {
  const visits = queryClient.fetchQuery(["/visit/count"], () =>
    get("/visit/count")
  );

  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const userInfo = queryClient.fetchQuery(["/user"], () => get("/user"), {
    staleTime: 1000 * 60 * 2,
  });

  const rewards = queryClient.fetchQuery(["/rewards"], () =>
    get("/rewards", {})
  );

  const membershipFeatures = queryClient.fetchQuery(["/shop/memberships"], () =>
    get("/shop/memberships")
  );

  const memberships = queryClient.fetchQuery(["/memberships"], () =>
    get("/memberships")
  );

  const listings = queryClient.fetchQuery(
    ["/listings"],
    () => get("/listings"),
    { staleTime: 1000 * 60 }
  );

  const promotions = queryClient.fetchQuery(
    ["/promotions"],
    () => get("/promotions"),
    { staleTime: 1000 * 60 * 2 }
  );

  const recommendations = queryClient
    .fetchQuery<Models<"AdonisTopRecommendationModel">[]>(
      ["/adonis/top-recommendations"],
      () => get("/adonis/top-recommendations", { limit: 5 }),
      { staleTime: 1000 * 30 }
    )
    .catch((error) => {
      console.error("Failed to fetch recommendations:", error);
      return []; // Return empty array on failure to allow page to load
    });

  const repeatmdOffer = queryClient.fetchQuery(["/offers"], () =>
    get("/offers")
  );

  const platformLocation = queryClient.fetchQuery(["/platform-location"], () =>
    get("/platform-location")
  );

  const theme = queryClient.fetchQuery(["/platform/theme"], () =>
    get("/platform/theme")
  );

  const customCategoriesList = queryClient.fetchQuery(
    ["/shop/custom-categories/listings"],
    () =>
      get("/shop/custom-categories/listings").then((listings) =>
        listings.filter(
          (listing) =>
            listing.promotion?.expiresOn == null ||
            new Date(listing.promotion.expiresOn) > new Date()
        )
      ),
    { staleTime: 1000 * 10 }
  );

  const authClaims = queryClient.fetchQuery(["/auth/claims"], () =>
    get("/auth/claims")
  );

  const shopDiscounts = queryClient.fetchQuery(
    ["/shop/discounts"],
    () => get("/shop/discounts"),
    { staleTime: 1000 * 10 }
  );

  const giftCardListings = queryClient.fetchQuery(
    ["/gift-cards"],
    () => get("/gift-cards", { FilterActiveOnly: true }),
    { staleTime: 1000 * 10 }
  );

  return waitObj({
    visits: visits.then((res) => res.totalVisits),
    platform,
    userInfo: Promise.all([userInfo, authClaims]).then(
      async ([user, { claims }]) => {
        const deviceInfo = await getDeviceInfo();

        Analytics.identify(user.id, {
          firstName: user.firstName,
          lastName: user.lastName,
          phoneNumber: user.phoneNumber || "",
          email: user.email || "",
          subdomain: user.platformDomain?.domain || "",
          platformId: user.platformDomain?.platformId || "",
          platformUserId: claims.platformUserId,
          role: "customer",
          environment: import.meta.env.MODE,
          version: "v2",
          ...deviceInfo,
        });

        return user;
      }
    ),
    rewards,
    membershipFeatures,
    memberships,
    listings,
    promotions,
    platformLocation,
    recommendations,
    repeatmdOffer,
    theme,
    customCategoriesList,
    shopDiscounts,
    giftCardListings,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const homeLoader: LoaderFunction = () => {
  return load();
};

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    platform,
    userInfo,
    rewards,
    membershipFeatures,
    memberships,
    listings,
    promotions,
    platformLocation,
    recommendations,
    repeatmdOffer,
    theme,
    customCategoriesList,
    shopDiscounts,
    giftCardListings,
  } = useLoaderData() as LoaderData;
  const {
    openChat,
    isEnabled: isAdonisEnabled,
    isPersonalisationEnabled,
  } = useAdonisChat();
  const { checkinUser } = useCheckin();

  const flagGiftCards = useFeatureFlag("giftcards");
  const flagSiteWidePromotions = useFeatureFlag("site-wide-promotions");
  const isSellingProductsEnabled = useFeatureFlag("selling-products");
  const isV3Enabled = true;

  const { data: areRetailProductsVisible } = useQuery({
    queryKey: ["/retailproducts/are-retail-products-visible"],
    queryFn: () => get("/retailproducts/are-retail-products-visible"),
  });

  const flagRepeatmdGlp = useFeatureFlag("repeatmd-glp");
  const { dunningMembership } = useDunningMembership();
  const [showAffirmModal, setShowAffirmModal] = useState(false);
  const [shouldAnimateFromPersonalisation] = useState(() => {
    // Check URL parameter on initial mount only
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.has("fromPersonalisation");
  });
  const [showCountUpAnimation, setShowCountUpAnimation] = useState(false);

  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
  });

  const { preferences } = useUserPreferences();

  const { mutate: recordReferralSignupCompleted } = useMutation({
    mutationFn: (statelessCode: string) =>
      post("/incentives/record-referral-signup-completed", {
        statelessCode: statelessCode,
        personId: user?.id as string,
        platformId: user?.platform?.id as string,
        platformLocationId: user?.platformLocation?.id as string,
      }),
  });

  useEffect(() => {
    const statelessCode = LocalStorageService.getItem("statelessReferralCode");

    if (statelessCode && user?.id && user?.platform?.id) {
      recordReferralSignupCompleted(statelessCode);
    }
  }, [user]);

  // Process stored scanned code after user authentication
  const processStoredScannedCode = useCallback(async () => {
    const storedScannedCode = localStorage.getItem("scannedCode");

    if (storedScannedCode && (await Services.auth.isAuthorized())) {
      console.log("Processing stored scanned code after authentication");
      checkinUser(storedScannedCode);
      // Clean up stored data
      localStorage.removeItem("scannedCode");
      localStorage.removeItem("scannedPlatformId");
    }
  }, [checkinUser]);

  const heroImage = platform?.homeImageUrl
    ? platform?.homeImageUrl
    : theme.resources?.find((resource) => resource.key === "homepage-hero")
        ?.url;

  const browseByConcernImage = theme.resources?.find(
    (resource) => resource.key === "browse-by-concern-campaign"
  )?.url;

  const membershipFeature = membershipFeatures?.[0];

  const shouldShowRewards = platform?.platformSettings?.rewardsEnabled;
  const shouldShowSpendBasedRewards =
    platform?.platformSettings?.spendBasedRewardsEnabled ?? true;

  const available = rewards?.filter(
    ({ status }) => status === RewardStatus.Available
  );

  const future = rewards?.filter(
    ({ status }) => status === RewardStatus.NotAvailable
  );

  const nextReward = future?.length
    ? future.reduce(
        (acc, curr) =>
          acc.minimumVisits ?? 0 <= (curr.minimumVisits ?? 0) ? acc : curr,
        future[0]
      )
    : undefined;

  const adonisHomeCTAText = useFeatureFlag("adonisHomeCTAText");
  const adonisV3CTAText = useFeatureFlag("adonisV3CTAText");
  const adonisRecommandationCarouselText = useFeatureFlag(
    "adonisRecommandationCarouselText"
  );
  const { showAdonisDynamicCTABanner, hideCTABanner } =
    useAdonisDynamicCTABanner(isAdonisEnabled);

  const handleAdonisCTAClick = () => {
    openChat();
    trackAdonisViewed();
  };

  const trackAdonisViewed = (): void => {
    Analytics.track(AnalyticsEvents.ADONIS_VIEWED, {});
  };

  const topSellingTreatmentCards = getListingsWithDiscountInformation(
    listings,
    customCategoriesList,
    shopDiscounts?.listingDiscounts
  )
    .filter(
      ({ type }) => type !== "Product" && type !== "ProductFromExternalSource"
    )
    .sort((a, b) => b.sellVolume - a.sellVolume)
    .map(
      ({
        id,
        type,
        title,
        imageUrl,
        baseCost,
        membershipCost,
        discountText,
        customColor,
      }) => {
        if (!imageUrl || !title || !baseCost) {
          return null;
        }
        return {
          to: `/pdp/package/${id}`,
          imageSrc: imageUrl,
          tag: type,
          title,
          price: baseCost,
          memberPrice: membershipCost,
          discount: discountText,
          customColor,
        };
      }
    )
    .filter(notFalsy)
    .slice(0, 5);

  const specialOffersCards = promotions
    .filter((promotion) => !promotion.redeemedOn)
    .map(
      (promotion) =>
        promotion && {
          to: userInfo.isGuest
            ? "/signin"
            : "/discover?promotion=" + promotion?.id,
          tag: "Promotion",
          title: promotion.title,
          imageSrc: promotion.imageUrl ?? "",
          primary: userInfo.isGuest ? "Sign up to use offer" : "View Offer",
          alert:
            promotion.expiresOn &&
            `Expires ${dayjs(promotion.expiresOn).fromNow()}`,
        }
    )
    .slice(0, 5);
  const marketplaceEnabled = platform.platformSettings?.marketplaceEnabled;

  const sortedCustomCategoryPromotions = [...customCategoriesList]
    .sort(
      (a, b) =>
        (a.promotion?.priority ?? 1) - (b.promotion?.priority ?? 1) ||
        dayjs(a.promotion?.expiresOn).valueOf() -
          dayjs(b.promotion?.expiresOn).valueOf()
    )
    .filter(({ promotion }) => promotion !== null);

  const categoryWithPromotion = sortedCustomCategoryPromotions.find(
    ({ promotion, category, categoryInclusions }) =>
      promotion && category && !!categoryInclusions.length
  );

  const bannerAffirmShow =
    platform.platformSettings?.marketplaceEnabled &&
    platform.platformSettings?.affirmEnabled;

  const bannerSitewidePromotionShow = !!categoryWithPromotion;

  const bannerContainersShow = dunningMembership.status === false;

  const promotion = categoryWithPromotion?.promotion;
  const category = categoryWithPromotion?.category;

  // Process stored scanned code on component mount
  useEffect(() => {
    processStoredScannedCode();
  }, [processStoredScannedCode]);

  // Handle fromSignIn query parameter
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has("fromSignIn")) {
      window.localStorage.removeItem("state");
      queryParams.delete("fromSignIn");
    }
  }, []);

  useEffect(() => {
    if (showCountUpAnimation) {
      const timer = setTimeout(() => {
        setShowCountUpAnimation(false);
      }, 1500); // Match animation duration

      return () => clearTimeout(timer);
    }
  }, [showCountUpAnimation]);

  const handleReferralRewardAcknowledged = () => {
    setShowCountUpAnimation(true);
  };

  const bookAppointmentAction = getBookAppointmentAction(
    platformLocation.bookAppointmentUrl,
    platformLocation.phoneNumber,
    platformLocation.email
  );

  const shouldShowCategoryPromotionInCarousel =
    flagSiteWidePromotions && category && promotion?.imageUrl != null;

  let carouselSlideIndex;
  if (shouldShowCategoryPromotionInCarousel && flagRepeatmdGlp) {
    carouselSlideIndex = 2;
  } else if (shouldShowCategoryPromotionInCarousel || flagRepeatmdGlp) {
    carouselSlideIndex = 1;
  } else {
    carouselSlideIndex = 0;
  }

  function handleContactCardButtonOnclick(button: "primary" | "secondary") {
    if (button == "primary") {
      if (bookAppointmentAction?.includes("tel:")) {
        window.open(bookAppointmentAction);
      } else if (bookAppointmentAction) {
        Browser.open({ url: bookAppointmentAction });
      }
    } else if (
      button == "secondary" &&
      platformLocation?.leaveReviewUrl != null
    ) {
      Browser.open({ url: platformLocation.leaveReviewUrl });
    }
  }

  const retailProducts = listings
    .filter(
      ({ type }) => type === "Product" || type === "ProductFromExternalSource"
    )
    .slice(0, 4);

  const recommendationsWithDiscounts = getListingsWithDiscountInformation(
    recommendations,
    customCategoriesList,
    shopDiscounts?.listingDiscounts
  );

  const transformedRecommendations = transformRecommendationsToSlides(
    recommendationsWithDiscounts
  );

  const renderTopNav = () => {
    if (isV3Enabled && shouldShowRewards) {
      return (
        <AnimatedBackground mode="light-prominent">
          {renderBanners()}
          <TopNavV3
            hideShadow
            withAffirmOnTop={bannerContainersShow}
            backgroundVariant="transparent"
          >
            <img
              alt="Platform logo."
              className="h-8"
              src={platform?.logoImageUrl || ""}
            />
          </TopNavV3>

          <RewardProgressV2
            displayGreetings
            nextMilestone={nextReward?.minimumVisits || 0}
            rewardsCount={available?.length || 0}
            cashToUnlock={nextReward?.cashToUnlock}
            unlockProgressPercent={
              future.length == 0 ? 100 : nextReward?.unlockProgressPercent || 0
            }
            marketplaceEnabled={platform.platformSettings?.marketplaceEnabled}
            visitCashValue={platform.platformSettings?.visitCashValue}
            spendBasedRewardsEnabled={shouldShowSpendBasedRewards}
            canCollapse
            showCountUpAnimation={showCountUpAnimation}
          />
        </AnimatedBackground>
      );
    }

    return (
      <>
        {renderBanners()}
        <TopNav hideShadow withAffirmOnTop={bannerContainersShow}>
          <img
            alt="Platform logo."
            className="h-8"
            src={platform?.logoImageUrl || ""}
          />
        </TopNav>
        {shouldShowRewards && (
          <div className="relative rounded-b-lg bg-white">
            <RewardProgress
              displayGreetings
              nextMilestone={nextReward?.minimumVisits || 0}
              rewardsCount={available?.length || 0}
              cashToUnlock={nextReward?.cashToUnlock}
              unlockProgressPercent={
                future.length == 0
                  ? 100
                  : nextReward?.unlockProgressPercent || 0
              }
              marketplaceEnabled={platform.platformSettings?.marketplaceEnabled}
              visitCashValue={platform.platformSettings?.visitCashValue}
              spendBasedRewardsEnabled={shouldShowSpendBasedRewards}
            />
          </div>
        )}
      </>
    );
  };

  const { data: referralRewardForPlatformLocation } = useQuery({
    queryKey: [
      "/incentives/{platformLocationId}/available-referral-reward",
      platform?.id ?? "",
    ],
    queryFn: () =>
      get("/incentives/{platformLocationId}/available-referral-reward", {
        platformLocationId: user?.platformLocation?.id ?? "",
      }).catch(() => null),
  });

  const renderBanners = () => {
    return (
      bannerContainersShow && (
        <>
          <div className="sticky top-0 z-10 h-[env(safe-area-inset-top)] bg-white"></div>
          <BannersContainer>
            {bannerAffirmShow && (
              <AffirmBanner
                onClick={() => setShowAffirmModal(true)}
                hideRewardText={!shouldShowRewards}
              />
            )}
            {flagSiteWidePromotions && bannerSitewidePromotionShow && (
              <Banner
                titleColor={promotion?.textColor ?? undefined}
                title={promotion?.title}
                backgroundColor={promotion?.backgroundColor ?? undefined}
                summary={promotion?.description ?? undefined}
                onClick={() =>
                  navigate(`/shop/custom-category/${category?.id}`)
                }
                arrow
              ></Banner>
            )}

            {isV3Enabled && !!referralRewardForPlatformLocation && (
              <ReferralBanner />
            )}
          </BannersContainer>
        </>
      )
    );
  };

  const renderAffirmModal = () => {
    return (
      platform.platformSettings?.affirmEnabled && (
        <AffirmModal
          platformLogo={platform?.logoImageUrl || ""}
          open={showAffirmModal}
          onClose={() => setShowAffirmModal(false)}
          onShopNow={
            platform.platformSettings?.marketplaceEnabled
              ? () => navigate("/shop")
              : null
          }
          onBookAppointment={
            !platform.platformSettings?.marketplaceEnabled
              ? // platformLocation.phoneNumber might be null, at least on local/non-initialised environemnts
                () =>
                  platformLocation.phoneNumber !== null
                    ? window.open(`tel:${platformLocation.phoneNumber}`)
                    : null
              : null
          }
          hideRewardText={!shouldShowRewards}
        />
      )
    );
  };

  const renderSiteWidePromotion = () => {
    return (
      flagSiteWidePromotions &&
      promotion &&
      category && (
        <ShowSiteWidePromotion promotion={promotion} categoryId={category.id} />
      )
    );
  };

  const renderGiftCardAcknowledgeModal = () => {
    return flagGiftCards && <GiftCardAcknowledgeModal />;
  };

  return (
    <motion.div
      initial={shouldAnimateFromPersonalisation ? { opacity: 0, y: 30 } : false}
      animate={shouldAnimateFromPersonalisation ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: ANIMATION_DURATION,
        ease: ANIMATION_EASE,
      }}
    >
      {renderTopNav()}

      <div className="sticky top-[calc(50px_+_env(safe-area-inset-top))] z-10 -mt-1 h-1 " />

      {renderAffirmModal()}

      {renderSiteWidePromotion()}

      {renderGiftCardAcknowledgeModal()}

      <Carousel
        showBreadCrumb={flagSiteWidePromotions && promotion?.imageUrl != null}
      >
        {shouldShowCategoryPromotionInCarousel && (
          <div className="min-w-full snap-center" data-slideindex={0}>
            <div>
              <PromotionHero
                {...promotion}
                giftCardListings={giftCardListings}
                categoryId={category.id}
                homeStyle
              />
            </div>
          </div>
        )}
        {flagRepeatmdGlp && (
          <div className="min-w-full snap-center" data-slideindex={1}>
            <BannerCTA
              homeStyle
              title="Introducing Repeat Health"
              description="Medical weight loss"
              callToActionText="Take the quiz"
              navigateTo="/health/glp/questionnaire"
            />
          </div>
        )}
        <div
          className="min-w-full snap-center"
          data-slideindex={carouselSlideIndex}
        >
          {(() => {
            const isTextEmptyOrSingleChar = (
              text: string | null | undefined
            ): boolean => {
              if (!text) return true;
              const trimmed = text.trim();
              return trimmed.length === 0 || trimmed.length === 1;
            };

            const shouldHideGradient =
              isTextEmptyOrSingleChar(platform.homeTitle) &&
              isTextEmptyOrSingleChar(platform.homeSubtitle);

            const backgroundImage = shouldHideGradient
              ? `url("${heroImage}")`
              : `linear-gradient(180deg, rgba(0, 0, 0, 0) 20.74%, rgba(0, 0, 0, 0) 39.17%, rgba(0, 0, 0, 0.35) 88.48%), url("${heroImage}")`;

            return (
              <div
                className="-mt-3 flex aspect-[390/434] flex-col bg-cover bg-center p-8 text-white"
                style={{
                  backgroundImage,
                }}
              >
                <div className="mt-auto">
                  <div className="pb-4 text-center font-serif text-display text-shadow">
                    {platform.homeTitle}
                  </div>
                  <div className="text-center font-sans text-bold2 text-shadow">
                    {platform.homeSubtitle}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </Carousel>
      {marketplaceEnabled && membershipFeature && !!memberships?.length && (
        <MembershipPromo
          {...membershipFeature}
          memberships={memberships}
          useAnimatedBackground={isV3Enabled}
        />
      )}
      {isPersonalisationEnabled &&
        (preferences?.concernIds && !!preferences.concernIds.length ? (
          !!recommendations.length && (
            <AdvisorRecommandationCarousel
              recommendations={transformedRecommendations}
              ctaText={adonisRecommandationCarouselText}
              onAskClick={handleAdonisCTAClick}
              onViewMoreClick={() => navigate("/shop/recommended-treatments")}
            />
          )
        ) : (
          <AdvisorMissingOutCarousel
            buttonColor="#BDA280"
            onButtonClick={() => navigate(PersonalisationIntroContainer_PATH)}
          />
        ))}
      {platform.platformSettings?.marketplaceEnabled && (
        <>
          {isAdonisEnabled && !isPersonalisationEnabled && (
            <div>
              <AdonisCTAVariation1
                onClick={handleAdonisCTAClick}
                ctaText={adonisHomeCTAText}
              />
            </div>
          )}

          {!!listings.length && (
            <div className="bg-one">
              <ProductCarousel
                svg={Star}
                title="Explore Our Best-Selling Services"
                description="Your favorite treatments and expertly curated packages—all in one place."
                cards={topSellingTreatmentCards}
                primary="Explore all services"
                onButtonClick={() => navigate("/shop")}
                guestMode={userInfo.isGuest}
              />
            </div>
          )}
          {platform.platformSettings?.sellingProductsEnabled &&
            isSellingProductsEnabled &&
            areRetailProductsVisible && (
              <RetailProductsCTA retailProducts={retailProducts} />
            )}
          {!!specialOffersCards.length && (
            <div className="bg-two">
              <ProductCarousel
                svg={Discover}
                title="Special Offers"
                cards={specialOffersCards}
                primary="Discover more offers"
                onButtonClick={() => {
                  if (userInfo.isGuest) {
                    navigate("/signin");
                  } else {
                    navigate("/discover");
                  }
                }}
              />
            </div>
          )}
        </>
      )}
      {isPersonalisationEnabled && (
        <AdvisorCTA
          ctaText={adonisV3CTAText}
          onChatClick={handleAdonisCTAClick}
          showLink={preferences?.concernIds && !!preferences.concernIds.length}
          onLinkClick={() => navigate("/shop/recommended-treatments")}
        />
      )}

      {!isAdonisEnabled &&
        !isPersonalisationEnabled &&
        checkIfListingsHaveConcerns(listings) && (
          <CampaignCard
            imageSrc={browseByConcernImage}
            title="Not sure what you need?"
            subtitle="Browse by concern"
            description="Explore our wide selection of effective treatment options, organized by specific concern, to address your unique skin needs and goals."
            svg={Quiz}
            primary="Search by concern"
            onButtonClick={() => navigate("/shop?concerns=true")}
          />
        )}

      <div className="bg-white">
        <CampaignCard
          variant="contactUs"
          subtitle="connect with us"
          imageSrc={platformLocation.imageUrl}
          logoSrc={platform.logoImageUrl}
          address={formatAddressNoCountry(
            platformLocation?.address1,
            platformLocation?.address2,
            platformLocation?.city,
            platformLocation?.state,
            platformLocation?.postalCode
          )}
          email={platformLocation?.email ?? ""}
          phone={formatPhoneNumber(platformLocation?.phoneNumber)}
          primary="Schedule your visit"
          secondary={
            platformLocation?.leaveReviewUrl ? "Leave us a review" : null
          }
          onButtonClick={handleContactCardButtonOnclick}
          primaryDisabled={!bookAppointmentAction}
        />
      </div>
      {repeatmdOffer && platform.platformSettings?.repeatMDMarketingEnabled && (
        <div className="bg-two">
          <CampaignCard
            imageSrc={repeatmdOffer.imageUrl}
            title={repeatmdOffer.title}
            description={repeatmdOffer.shortDescription}
            primary={userInfo.isGuest ? "Sign up to learn more" : "Learn more"}
            onButtonClick={() => {
              if (userInfo.isGuest) {
                navigate("/signin");
              } else {
                navigate("/discover?repeatOffer=" + repeatmdOffer.id);
              }
            }}
          />
        </div>
      )}

      <>
        <ReferAFriend />
        <ReferralRewardsCard />
      </>

      <AnimatePresence>
        {isAdonisEnabled && showAdonisDynamicCTABanner && (
          <motion.div
            className="z-1 sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <AdonisDynamicCTABanner
              onClick={handleAdonisCTAClick}
              onClose={hideCTABanner}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <ReferralRewardsAcknowledgeRewardsModal
        onCloseAfterInteracting={handleReferralRewardAcknowledged}
        platformId={platform?.id ?? ""}
      />
    </motion.div>
  );
};

export default HomePage;
